import React, { useRef, useState } from 'react'
import { useIntersectionObserver } from 'usehooks-ts'
import { makeStyles } from 'tss-react/mui'
import Carousel from 'nuka-carousel'
import { ButtonBase, Container, Typography } from '@mui/material'
import CarouselArrowLeftIcon from '@icons/CarouselArrowLeftIcon'
import CarouselArrowRightIcon from '@icons/CarouselArrowRightIcon'
import ReplacementProductCard, { MainProduct } from './ReplacementProductCard'
import useResponsiveSizes from '@utils/useResponsiveSizes'
import {
  handleSlideMovement,
  ProductCarouselControlProps,
} from '../DynamicProductCarousel/slideMovementHandler'
import {
  CarouselProduct,
  CarouselProductTypes,
} from '../DynamicProductCarousel/DynamicProductCardTypes'
import { sendCarouselPromoEvent } from '@src/utils/analytics/carouselAnalytics'
import { useCategorizePage } from '@utils/analytics/CategorizePage'
import { INTERSECTION_OBSERVER_THRESHOLD } from '../DynamicProductCarousel/DynamicProductCarousel'

interface RecommendedProductCarouseControlProps {
  products: CarouselProduct[]
  creativeSlot: string
  carouselType: CarouselProductTypes
  pageType: string
  pageTypeDetails?: string | null | undefined
}

const useStyles = makeStyles<void, 'contentCondensed'>({
  name: 'ReplacementProductsCarousel',
  uniqId: 'RepProdCarousel',
})((theme, _params, classes) => {
  return {
    withoutControls: {
      padding: 0,
    },
    content: {
      paddingTop: theme.spacing(8),
      paddingBottom: theme.spacing(8),

      // Adds visible keyboard focus since the div element is tabbable (though not interactive)
      '& .slider-frame': {
        border: '2px solid transparent',
        '&:focus-visible': {
          border: `2px solid ${theme.palette.primary.main}`,
          borderRadius: theme.shape.borderRadius,
        },
      },
      [theme.breakpoints.up('sm')]: {
        paddingBottom: theme.spacing(8),
      },
    },
    contentCondensed: {
      padding: 0,
    },
    title: {
      marginBottom: theme.spacing(8),
      [`.${classes.contentCondensed} &`]: {
        marginBottom: theme.spacing(4),
      },
    },
    navBtn: {},
    navBtnSmall: {
      height: 24,
      width: 24,
    },
    navBtnLarge: {
      height: 32,
      width: 32,
      display: 'none',
      [theme.breakpoints.up('sm')]: {
        display: 'flex',
      },
    },
    navBtnLeft: {
      transform: 'translateX(-40px)',
    },
    mobileBottomControls: {
      width: 105,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      position: 'relative',
      bottom: -44,
      [theme.breakpoints.up('xs')]: {
        bottom: -25,
      },
    },
    icon: {
      fontSize: theme.typography.pxToRem(14),
      color: theme.palette.primary.main,
      width: '1em',
    },
    iconDisabled: {
      color: theme.palette.grey[500],
    },
    cartRecommended: {
      backgroundColor: theme.palette.background.grey,
      marginBottom: theme.spacing(20),
    },
    card: {
      [theme.breakpoints.up('sm')]: {
        paddingRight: theme.spacing(10),
      },
    },
  }
})

const useRecommendedProductCarouselControls = ({
  products,
  creativeSlot,
  carouselType,
  pageType,
  pageTypeDetails,
}: RecommendedProductCarouseControlProps) => {
  const { classes, cx } = useStyles()

  return {
    renderCenterLeftControls: (props: ProductCarouselControlProps) => {
      const disabled = props.currentSlide === 0
      return (
        <ButtonBase
          className={cx(
            classes.navBtn,
            classes.navBtnLarge,
            classes.navBtnLeft
          )}
          disabled={disabled}
          onClick={() =>
            handleSlideMovement(
              props,
              products,
              'prev',
              creativeSlot,
              carouselType,
              pageType,
              pageTypeDetails
            )
          }
          aria-label="previous"
        >
          <CarouselArrowLeftIcon
            className={cx(classes.icon, {
              [classes.iconDisabled]: disabled,
            })}
          />
        </ButtonBase>
      )
    },
    renderCenterRightControls: (props: ProductCarouselControlProps) => {
      const disabled =
        props.currentSlide + props.slidesToShow === props.slideCount ||
        props.slideCount < props.slidesToShow
      return (
        <ButtonBase
          disabled={disabled}
          className={cx(classes.navBtn, classes.navBtnLarge)}
          onClick={() =>
            handleSlideMovement(
              props,
              products,
              'next',
              creativeSlot,
              carouselType,
              pageType,
              pageTypeDetails
            )
          }
          aria-label="next"
        >
          <CarouselArrowRightIcon
            className={cx(classes.icon, {
              [classes.iconDisabled]: !!props.slidesToScroll && disabled,
            })}
          />
        </ButtonBase>
      )
    },
    renderBottomCenterControls: (props: ProductCarouselControlProps) => {
      const previousDisabled = props.currentSlide === 0
      const nextDisabled =
        props.currentSlide + props.slidesToShow === props.slideCount ||
        props.slideCount < props.slidesToShow
      return (
        <div className={classes.mobileBottomControls}>
          <ButtonBase
            disabled={previousDisabled}
            onClick={() =>
              handleSlideMovement(
                props,
                products,
                'prev',
                creativeSlot,
                carouselType,
                pageType,
                pageTypeDetails
              )
            }
            aria-label="previous"
            className={cx(classes.navBtn, classes.navBtnSmall)}
          >
            <CarouselArrowLeftIcon
              className={cx(classes.icon, {
                [classes.iconDisabled]: previousDisabled,
              })}
            />
          </ButtonBase>
          <Typography variant="body2">
            {props.currentSlide + 1} of {props.slideCount}
          </Typography>
          <ButtonBase
            disabled={nextDisabled}
            onClick={() =>
              handleSlideMovement(
                props,
                products,
                'next',
                creativeSlot,
                carouselType,
                pageType,
                pageTypeDetails
              )
            }
            aria-label="next"
            className={cx(classes.navBtn, classes.navBtnSmall)}
          >
            <CarouselArrowRightIcon
              className={cx(classes.icon, {
                [classes.iconDisabled]: !!props.slidesToScroll && nextDisabled,
              })}
            />
          </ButtonBase>
        </div>
      )
    },
  }
}

interface CarouselProps {
  type: CarouselProductTypes
  withoutControls?: boolean
  slidesToShow?: number
  condensed?: boolean
  preloadCardImages?: boolean
  products: CarouselProduct[]
  mainProduct?: MainProduct
}

const ReplacementProductsCarousel: React.FC<CarouselProps> = ({
  type,
  products,
  withoutControls,
  slidesToShow,
  condensed,
  preloadCardImages,
  mainProduct,
}) => {
  const { classes, cx } = useStyles()
  const getSize = useResponsiveSizes()
  const pageCategory = useCategorizePage()

  const creativeSlot = `${type} - ${pageCategory}`

  const carouselControls = useRecommendedProductCarouselControls({
    products,
    creativeSlot,
    carouselType: type,
    pageType: pageCategory,
  })
  const isCartRecommended =
    type === CarouselProductTypes.CartRecommendedProducts
  const numProducts = getSize({ xs: 1, sm: 2, lg: 3 })

  const carouselRef = useRef(null)
  const [sawCarousel, setSawCarousel] = useState(false)
  const carouselVisbility = useIntersectionObserver(carouselRef, {
    threshold: INTERSECTION_OBSERVER_THRESHOLD,
  })

  // Kick out and don't render anything if no product data.
  if (!products || !products?.length) return null

  if (carouselVisbility?.isIntersecting && !sawCarousel) {
    sendCarouselPromoEvent({
      creativeSlot,
      carouselType: type,
      products: products,
    })

    setSawCarousel(true)
  }

  return (
    <div
      data-testid={type}
      id={type}
      className={cx({
        [classes.cartRecommended]: isCartRecommended,
      })}
      ref={carouselRef}
    >
      <Container
        maxWidth="lg"
        className={cx({
          [classes.withoutControls]: withoutControls,
        })}
      >
        <div
          className={cx(classes.content, {
            [classes.contentCondensed]: condensed,
          })}
        >
          <Carousel
            slidesToScroll={slidesToShow || numProducts}
            slidesToShow={slidesToShow || numProducts}
            disableEdgeSwiping
            withoutControls={withoutControls}
            {...carouselControls}
          >
            {products.map((product, i) => (
              <ReplacementProductCard
                key={i}
                mainProduct={mainProduct}
                product={product}
                testId={type}
                preloadCardImages={preloadCardImages}
                className={classes.card}
                index={i}
                type={type}
              />
            ))}
          </Carousel>
        </div>
      </Container>
    </div>
  )
}

export default ReplacementProductsCarousel
